import { Dropdown, Menu, Table, TableColumnsType } from "antd";
import NextIcon from "components/Icons/NextIcon";
import PreviousIcon from "components/Icons/PreviousIcon";
import CountUp from "react-countup";
import moment from "moment";
import AlterBy from "pages/DriverWalletPages/DriverWalletDetail/components/AlterBy";
import WalletStatus from "pages/DriverWalletPages/DriverWalletDetail/components/WalletStatus";
import { IAccountFinance } from "models/account";
import { useMemo, useState } from "react";
import useFinanceAccountDashboard from "hooks/useFinanceAccountDashboard";

export type IFinanceFilter = "Daily" | "Weekly" | "Monthly" | "Yearly";

export type IAccountFinanceFilter =
  | "Today"
  | "Yesterday"
  | "This Week"
  | "Last Week"
  | "This Month"
  | "Last Month"
  | "This Year"
  | "Last Year";

interface AccountFinanceFilterButtonProps {
  handleFilterChange: (value: IAccountFinanceFilter) => void;
  defaultValue?: IAccountFinanceFilter;
}

interface FinanceFilterButtonProps {
  handleFilterChange: (value: IFinanceFilter) => void;
  defaultValue?: IFinanceFilter;
  filterForAdminRole: boolean;
}

export function AccountFinanceFilterButton({
  handleFilterChange,
  defaultValue,
}: AccountFinanceFilterButtonProps) {
  const [value, setValue] = useState<IAccountFinanceFilter>(
    defaultValue ?? "Today"
  );
  const handleOnClick = (newValue: IAccountFinanceFilter) => {
    setValue(newValue);
    handleFilterChange(newValue);
  };

  const items = useMemo(() => {
    return [
      {
        key: "Today",
        onClick: () => handleOnClick("Today"),
        label: <Label text={"Today"} />,
      },
      {
        key: "Yesterday",
        onClick: () => handleOnClick("Yesterday"),
        label: <Label text={"Yesterday"} />,
      },
      {
        key: "This Week",
        onClick: () => handleOnClick("This Week"),
        label: <Label text={"This Week"} />,
      },
      {
        key: "Last Week",
        onClick: () => handleOnClick("Last Week"),
        label: <Label text={"Last Week"} />,
      },
      {
        key: "This Month",
        onClick: () => handleOnClick("This Month"),
        label: <Label text={"This Month"} />,
      },
      {
        key: "Last Month",
        onClick: () => handleOnClick("Last Month"),
        label: <Label text={"Last Month"} />,
      },
      {
        key: "This Year",
        onClick: () => handleOnClick("This Year"),
        label: <Label text={"This Year"} />,
      },
      {
        key: "Last Year",
        onClick: () => handleOnClick("Last Year"),
        label: <Label text={"Last Year"} />,
      },
    ];
  }, [handleFilterChange]); // eslint-disable-line

  return (
    <Dropdown overlay={<Menu items={items} />}>
      <button className="outline-none border-none text-dark-blue text-sm flex gap-2 items-center capitalize whitespace-nowrap">
        {value}
        <ArrowDownIcon />
      </button>
    </Dropdown>
  );
}

export function FinanceFilterButton({
  handleFilterChange,
  defaultValue,
  filterForAdminRole,
}: FinanceFilterButtonProps) {
  const [value, setValue] = useState<IFinanceFilter>(defaultValue ?? "Daily");
  const handleOnClick = (newValue: IFinanceFilter) => {
    setValue(newValue);
    handleFilterChange(newValue);
  };

  const items = useMemo(() => {
    const data = [
      {
        key: "Daily",
        onClick: () => handleOnClick("Daily"),
        label: <Label text={"Daily"} />,
      },
      {
        key: "Weekly",
        onClick: () => handleOnClick("Weekly"),
        label: <Label text={"Weekly"} />,
      },
    ];
    if (!filterForAdminRole) {
      data.push(
        ...[
          {
            key: "Monthly",
            onClick: () => handleOnClick("Monthly"),
            label: <Label text={"Monthly"} />,
          },
          {
            key: "Yearly",
            onClick: () => handleOnClick("Yearly"),
            label: <Label text={"Yearly"} />,
          },
        ]
      );
    }
    return data;
  }, [handleFilterChange]); // eslint-disable-line

  return (
    <Dropdown overlay={<Menu items={items} />}>
      <button className="outline-none border-none text-dark-blue text-sm flex gap-2 items-center capitalize whitespace-nowrap">
        {value}
        <ArrowDownIcon />
      </button>
    </Dropdown>
  );
}

function Label({ text }: { text: string }) {
  return (
    <span className="text-dark-gray text-sm py-1 px-1 font-medium capitalize">
      {text}
    </span>
  );
}

function ArrowDownIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M12 15L7 10H17L12 15Z" fill="currentColor" />
    </svg>
  );
}

export default function FinanceAccountDashboard() {
  const {
    data: { data, loading, page, per_page, total, total_deduct, total_pop_up },
    handlePageChange,
    handleFilterChange,
  } = useFinanceAccountDashboard();
  console.log(data);

  return (
    <div className="w-full">
      <div className="w-full px-6 flex flex-wrap-reverse gap-3">
        <div className="flex flex-col px-4 py-6 bg-white min-w-52 max-w-60 rounded-lg gap-y-5 w-full shadow-md">
          <div className="flex flex-col gap-y-4">
            <p className="text-base font-normal text-dark-gray whitespace-nowrap">
              Total Deposit
            </p>
            <h3 className="text-darker-gray font-semibold text-2xl">
              <CountUp end={total_pop_up} />
            </h3>
          </div>
        </div>
        <div className="flex flex-col px-4 py-6 bg-white min-w-52 max-w-60 rounded-lg gap-y-5 w-full shadow-md">
          <div className="flex flex-col gap-y-4">
            <p className="text-base font-normal text-dark-gray whitespace-nowrap">
              Total Withdraw
            </p>
            <h3 className="text-darker-gray font-semibold text-2xl">
              <CountUp end={total_deduct} />
            </h3>
          </div>
        </div>
        <div className="flex flex-col px-4 py-6 bg-white min-w-52 max-w-60 rounded-lg gap-y-5 w-full shadow-md">
          <div className="flex flex-col gap-y-4">
            <p className="text-base font-normal text-dark-gray whitespace-nowrap">
              Total Transaction
            </p>
            <h3 className="text-darker-gray font-semibold text-2xl">
              <CountUp end={total_pop_up - total_deduct} />
            </h3>
          </div>
        </div>
      </div>
      <div className="flex items-center justify-end pt-10 pr-6">
        <AccountFinanceFilterButton
          handleFilterChange={handleFilterChange}
          defaultValue="Today"
        />
      </div>
      <div className="w-full mt-8 px-4">
        <Table
          loading={loading}
          columns={columns}
          dataSource={data}
          onChange={handlePageChange}
          pagination={{
            total,
            pageSize: per_page,
            current: page,
            nextIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <NextIcon />
              </span>
            ),
            prevIcon: (
              <span className="flex items-center justify-center w-8 h-8 text-2xl">
                <PreviousIcon />
              </span>
            ),
          }}
        />
      </div>
    </div>
  );
}

const columns: TableColumnsType<IAccountFinance> = [
  {
    title: "Amount",
    dataIndex: "amount",
    render: (value: number) => <p>{value.toFixed(2)}</p>,
  },
  {
    title: "Status",
    dataIndex: "type",
    render: WalletStatus,
  },
  {
    title: "Alter By",
    render: AlterBy,
  },
  {
    title: "Date",
    dataIndex: "created_at",
    render: (value: string) => <p>{moment(value).format("ll")}</p>,
  },
];
